import type { TabViewPanelProps } from '@myosh/odin-components';
import clsx from 'clsx';
import { TabPanel, TabView as TabViewPrimeReact } from 'primereact/tabview';
import React, {
  forwardRef,
  type ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

export type TabViewProps = {
  activeIndex?: number;
  onTabChange?: (index: number) => void;
  scrollable?: boolean;
  className?: string;
  children?: ReactElement<TabViewPanelProps> | Array<ReactElement<TabViewPanelProps>>;
  id?: string;
  headerClassName?: string;
  hideBorder?: boolean;
  scrollToNextTab?: boolean;
};

export interface TabViewRef {
  setActiveTab: (index: number) => void;
}

const TabView = forwardRef<TabViewRef, TabViewProps>((props, ref) => {
  const {
    activeIndex: controlledActiveIndex = 0,
    scrollable = true,
    className = '',
    children,
    onTabChange,
    id,
    headerClassName,
    hideBorder = false,
    scrollToNextTab = false,
  } = props;
  const [activeIndex, setActiveIndex] = useState<number>(controlledActiveIndex);
  const tabViewRef = React.useRef<TabViewPrimeReact>(null);
  const activeScrollIndexRef = React.useRef<number>(0);
  const tabsRef = React.useRef<NodeListOf<HTMLElement> | null>(null);
  const pendingFrameRef = React.useRef<number | null>(null);

  useEffect(() => {
    setActiveIndex(controlledActiveIndex);

    requestAnimationFrame(() => {
      const tabViewElement = tabViewRef?.current?.getElement();
      const targetTab = tabViewElement?.querySelectorAll('.p-tabview-nav li')[controlledActiveIndex];

      targetTab?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      activeScrollIndexRef.current = controlledActiveIndex;
    });
  }, [controlledActiveIndex]);

  const tabPanels = useMemo(() => {
    // Reset tabs cache when children change
    tabsRef.current = null;

    return React.Children.map(children, (tab) => {
      if (!React.isValidElement(tab)) {
        return null;
      }

      return (
        <TabPanel className={id} header={tab.props.header} headerClassName={headerClassName}>
          {tab.props.children}
        </TabPanel>
      );
    });
  }, [children, headerClassName, id]);
  // Cleanup pending animation frames on unmount
  useEffect(() => {
    return () => {
      if (pendingFrameRef.current !== null) {
        cancelAnimationFrame(pendingFrameRef.current);
      }
    };
  }, []);

  const updateActiveIndex = (index: number) => {
    setActiveIndex(index);
  };

  useImperativeHandle(ref, () => ({
    setActiveTab: (index: number) => {
      updateActiveIndex(index);
    },
  }));

  const tabViewClassName = clsx('odin tab-view-component', className, {
    'hide-border': hideBorder,
  });

  const getCurrentVisibleTabIndex = useCallback((navContainer: HTMLElement, tabs: NodeListOf<HTMLElement>): number => {
    const containerLeft = navContainer.scrollLeft;
    const containerRight = containerLeft + navContainer.clientWidth;

    // Find the first tab whose left edge is within the visible area.
    for (let i = 0; i < tabs.length; i++) {
      const tabLeft = tabs[i].offsetLeft;

      if (tabLeft >= containerLeft - 1 && tabLeft < containerRight) {
        return i;
      }
    }

    return 0;
  }, []);

  const onTabChangeHandler = useCallback(
    (event: { index: number }) => {
      const index = event.index;
      if (activeIndex !== index) {
        setActiveIndex(index);
        onTabChange?.(index);
      }
    },
    [activeIndex, onTabChange]
  );

  const handleScrollButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, direction: 'left' | 'right') => {
      if (!scrollToNextTab) return;

      e.preventDefault();
      e.stopPropagation();

      // Cancel any pending frame
      if (pendingFrameRef.current !== null) {
        cancelAnimationFrame(pendingFrameRef.current);
      }

      pendingFrameRef.current = requestAnimationFrame(() => {
        pendingFrameRef.current = null; // Clear the ref when frame executes

        const tabViewElement = tabViewRef.current?.getElement();
        if (!tabViewElement) return;

        const navContainer = tabViewElement.querySelector('.p-tabview-nav-content') as HTMLElement;

        if (!navContainer) return;

        // Cache tabs on first access or when cache is invalidated and prevent race conditions
        if (!tabsRef.current || tabsRef.current.length !== navContainer.children.length) {
          tabsRef.current = navContainer.querySelectorAll('li[data-position]') as NodeListOf<HTMLElement>;
        }

        const tabs = tabsRef.current;
        if (!tabs.length) return;

        const currentIndex = getCurrentVisibleTabIndex(navContainer, tabs);

        const nextIndex =
          direction === 'left' ? Math.max(0, currentIndex - 1) : Math.min(tabs.length - 1, currentIndex + 1);

        const targetTab = tabs[nextIndex];
        if (!targetTab) return;

        const prevButton = tabViewElement.querySelector('.p-tabview-nav-prev') as HTMLElement | null;

        const chevronWidth = prevButton && prevButton.offsetParent !== null ? prevButton.offsetWidth : 24; // Fallback to 24px if prevButton is not found or not visible (first render)

        const left = Math.max(0, targetTab.offsetLeft - chevronWidth);

        navContainer.scrollTo({
          left,
          behavior: 'smooth',
        });
      });
    },
    [scrollToNextTab]
  );

  return (
    <TabViewPrimeReact
      id={id}
      activeIndex={activeIndex}
      scrollable={scrollable}
      pt={{
        inkbar: { style: { display: 'none' } },
        nextButton: {
          onClickCapture: (e) => handleScrollButtonClick(e, 'right'),
        },
        prevButton: {
          onClickCapture: (e) => handleScrollButtonClick(e, 'left'),
        },
      }}
      onTabChange={onTabChangeHandler}
      className={tabViewClassName}
      ref={tabViewRef}
    >
      {tabPanels}
    </TabViewPrimeReact>
  );
});

TabView.displayName = 'TabView';

export default TabView;
